import React, { useState, useEffect, useRef } from 'react';

// 🆕 LED Ring Widget Component (Separate Panel)
const LEDRingWidget = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        let animationId;

        const drawLEDRing = () => {
            const ringState = window.robotLEDState?.ring || { mode: "off", color: "#1c1d1c", brightness: 100, rotateSpeed: 0.05, leds: Array(12).fill("#1c1d1c") };

            // Clear canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (ringState.mode === "off") {
                // Draw inactive ring (gray circle)
                ctx.strokeStyle = "rgba(100, 100, 100, 0.3)";
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.arc(80, 80, 55, 0, Math.PI * 2);
                ctx.stroke();

                // Draw OFF text
                ctx.fillStyle = "rgba(100, 100, 100, 0.5)";
                ctx.font = "bold 12px Arial";
                ctx.textAlign = "center";
                ctx.fillText("LED Ring OFF", 80, 83);


                animationId = requestAnimationFrame(drawLEDRing);
                return;
            }

            const numLEDs = 12;
            const ringRadius = 55;
            const ledSize = 10;

            const time = Date.now() / 1000;

            for (let i = 0; i < numLEDs; i++) {
                // Calculate position (start at top - 12 o'clock)
                const angle = (i / numLEDs) * Math.PI * 2 - Math.PI / 2;
                const ledX = 80 + Math.cos(angle) * ringRadius;
                const ledY = 80 + Math.sin(angle) * ringRadius;

                // Determine color
                let ledColor;
                if (ringState.mode === "rainbow") {
                    const hue = ((i / numLEDs) * 360 + (time * 50)) % 360;
                    ledColor = `hsl(${hue}, 100%, 50%)`;
                } else if (ringState.mode === "individual" && Array.isArray(ringState.leds)) {
                    ledColor = ringState.leds[i] || ringState.color;
                } else {
                    ledColor = ringState.color;
                }

                // Draw LED with glow
                ctx.globalAlpha = ringState.brightness / 100;
                ctx.shadowBlur = 20;
                ctx.shadowColor = ledColor;
                ctx.fillStyle = ledColor;
                ctx.beginPath();
                ctx.arc(ledX, ledY, ledSize, 0, Math.PI * 2);
                ctx.fill();

                // White center highlight
                ctx.shadowBlur = 0;
                ctx.globalAlpha = 0.4;
                ctx.fillStyle = "white";
                ctx.beginPath();
                ctx.arc(ledX, ledY, ledSize / 3, 0, Math.PI * 2);
                ctx.fill();

                ctx.shadowBlur = 0;
                ctx.globalAlpha = 1;
            }

            animationId = requestAnimationFrame(drawLEDRing);
        };

        drawLEDRing();

        return () => {
            if (animationId) cancelAnimationFrame(animationId);
        };
    }, []);

    return (
        <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(41, 128, 185, 0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            zIndex: 100
        }}>

            <div style={{
                fontSize: '10px',
                fontWeight: '600',
                color: 'rgba(255, 255, 255, 0.9)',
                marginBottom: '5px',
                textAlign: 'center',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}>
                LED Ring Status
            </div>

            <canvas
                ref={canvasRef}
                width="160"
                height="160"
                style={{ display: 'block' }}
            />

        </div>
    );
};

// Main Component

const RobotControl = () => {
    const canvasRef = useRef(null);
    const [temperature, setTemperature] = useState(50);
    const [humidity, setHumidity] = useState(50);
    const [potentiometer, setPotentiometer] = useState(512);
    const [distance, setDistance] = useState(50);
    const [lineSensorLeft, setLineSensorLeft] = useState(500);
    const [lineSensorRight, setLineSensorRight] = useState(500);
    const [ledColor, setLedColor] = useState('#FF0000');
    const [serialOutput, setSerialOutput] = useState([]);

    // Register sensor API
    useEffect(() => {
        window.robotSensors = {
            getDistance: () => distance,
            getTemperature: () => temperature,
            getHumidity: () => humidity,
            getPotentiometer: () => potentiometer,
            getLineSensorLeft: () => lineSensorLeft,
            getLineSensorRight: () => lineSensorRight,
            setLedColor: (color) => {
                setLedColor(color);
                setSerialOutput(prev => [...prev, `LED: ${color}`]);
            },
            addSerialOutput: (text) => {
                const time = new Date().toLocaleTimeString();
                setSerialOutput(prev => [...prev, `[${time}] ${text}`]);
            }
        };
        return () => { window.robotSensors = null; };
    }, [distance, temperature, humidity, potentiometer, lineSensorLeft, lineSensorRight]);

    // DRAW OTTO ROBOT ON CANVAS
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        // Animation loop
        let animationId;
        let eyeOffset = 0;
        let eyeDirection = 1;
        let lastFrameTime = performance.now();

        const drawRobot = () => {
            const now = performance.now();
            const dt = Math.min(0.05, (now - lastFrameTime) / 1000);
            lastFrameTime = now;

            // 🔥 READ LED STATE FROM WINDOW
            const ledState = window.robotLEDState || { on: false, color: '#FF0000', brightness: 100 };
            const currentLedColor = ledState.on ? ledState.color : '#34495e'; // Gray when off
            const brightness = ledState.on ? (ledState.brightness / 100) : 0.2; // Dim when off

            // 🦿 READ SERVO STATE FROM WINDOW
            const servoState = window.robotServoState || {
                left: { current: 90, target: 90 },
                right: { current: 90, target: 90 },
                speed: 60
            };
            if (!window.robotServoState) {
                window.robotServoState = servoState;
            }
            const servoSpeed = Math.max(10, Math.min(240, Number(servoState.speed) || 60));
            const updateServo = (servo) => {
                if (!servo) return 90;
                if (!Number.isFinite(servo.current)) {
                    servo.current = Number.isFinite(servo.target) ? servo.target : 90;
                }
                if (!Number.isFinite(servo.target)) {
                    servo.target = servo.current;
                }
                const diff = servo.target - servo.current;
                if (Math.abs(diff) < 0.1) {
                    servo.current = servo.target;
                } else {
                    const step = Math.sign(diff) * servoSpeed * dt;
                    if (Math.abs(step) >= Math.abs(diff)) {
                        servo.current = servo.target;
                    } else {
                        servo.current += step;
                    }
                }
                return servo.current;
            };
            const leftLegAngle = Math.max(0, Math.min(180, updateServo(servoState.left)));
            const rightLegAngle = Math.max(0, Math.min(180, updateServo(servoState.right)));

            // Clear canvas
            ctx.clearRect(0, 0, width, height);

            // Center position
            const centerX = width / 2;
            const centerY = height / 2;

            // === ULTRASONIC SENSOR (TOP) ===
            ctx.fillStyle = '#3498db';
            ctx.fillRect(centerX - 60, centerY - 180, 120, 40);
            ctx.strokeStyle = '#2c3e50';
            ctx.lineWidth = 3;
            ctx.strokeRect(centerX - 60, centerY - 180, 120, 40);

            // Ultrasonic sensor "eyes"
            ctx.fillStyle = '#2c3e50';
            ctx.beginPath();
            ctx.arc(centerX - 30, centerY - 160, 12, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(centerX + 30, centerY - 160, 12, 0, Math.PI * 2);
            ctx.fill();

            // Ultrasonic sensor inner circles
            ctx.fillStyle = '#34495e';
            ctx.beginPath();
            ctx.arc(centerX - 30, centerY - 160, 8, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.arc(centerX + 30, centerY - 160, 8, 0, Math.PI * 2);
            ctx.fill();

            // === MAIN BODY (ARDUINO BOARD) ===
            ctx.fillStyle = '#2980b9';
            ctx.fillRect(centerX - 100, centerY - 120, 200, 150);
            ctx.strokeStyle = '#2c3e50';
            ctx.lineWidth = 4;
            ctx.strokeRect(centerX - 100, centerY - 120, 200, 150);

            // USB port (top)
            ctx.fillStyle = '#34495e';
            ctx.fillRect(centerX - 15, centerY - 125, 30, 10);

            // Pin headers (left side)
            for (let i = 0; i < 6; i++) {
                ctx.fillStyle = '#2c3e50';
                ctx.fillRect(centerX - 105, centerY - 100 + i * 20, 8, 15);
            }

            // Pin headers (right side)
            for (let i = 0; i < 6; i++) {
                ctx.fillStyle = '#2c3e50';
                ctx.fillRect(centerX + 97, centerY - 100 + i * 20, 8, 15);
            }

            // === LED MATRIX (EYES) ===
            // Left eye
            ctx.fillStyle = '#34495e';
            ctx.fillRect(centerX - 70, centerY - 90, 50, 50);
            ctx.strokeStyle = '#2c3e50';
            ctx.lineWidth = 2;
            ctx.strokeRect(centerX - 70, centerY - 90, 50, 50);

            // Left eye LED (animated) - NOW USING window.robotLEDState
            ctx.fillStyle = currentLedColor;
            ctx.globalAlpha = brightness;
            ctx.shadowBlur = ledState.on ? 15 : 0;
            ctx.shadowColor = currentLedColor;
            ctx.beginPath();
            ctx.arc(centerX - 45 + eyeOffset, centerY - 65, 15, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;

            // Right eye
            ctx.fillStyle = '#34495e';
            ctx.fillRect(centerX + 20, centerY - 90, 50, 50);
            ctx.strokeRect(centerX + 20, centerY - 90, 50, 50);

            // Right eye LED (animated) - NOW USING window.robotLEDState
            ctx.fillStyle = currentLedColor;
            ctx.globalAlpha = brightness;
            ctx.shadowBlur = ledState.on ? 15 : 0;
            ctx.shadowColor = currentLedColor;
            ctx.beginPath();
            ctx.arc(centerX + 45 + eyeOffset, centerY - 65, 15, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;

            // === BUZZER ===
            ctx.fillStyle = '#e74c3c';
            ctx.beginPath();
            ctx.arc(centerX, centerY - 10, 20, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = '#c0392b';
            ctx.lineWidth = 3;
            ctx.stroke();

            // Buzzer pattern
            ctx.strokeStyle = '#c0392b';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.arc(centerX, centerY - 10, 12, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.arc(centerX, centerY - 10, 8, 0, Math.PI * 2);
            ctx.stroke();

            // === RGB LED (bottom center) - NOW USING window.robotLEDState ===
            ctx.fillStyle = currentLedColor;
            ctx.globalAlpha = brightness;
            ctx.shadowBlur = ledState.on ? 20 : 0;
            ctx.shadowColor = currentLedColor;
            ctx.beginPath();
            ctx.arc(centerX, centerY + 50, 15, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;

            // LED outline
            ctx.strokeStyle = '#2c3e50';
            ctx.lineWidth = 2;
            ctx.stroke();

            // === SERVOS (LEGS) ===
            const legWidth = 28;
            const legHeight = 62;
            const footWidth = 46;
            const footHeight = 16;

            const drawLeg = (hipX, hipY, angleDeg, mirror = false) => {
                ctx.save();
                ctx.translate(hipX, hipY);
                const rotation = ((angleDeg - 90) * Math.PI) / 180;
                ctx.rotate(mirror ? -rotation : rotation);

                ctx.fillStyle = '#7f8c8d';
                ctx.strokeStyle = '#2c3e50';
                ctx.lineWidth = 2;

                ctx.fillRect(-legWidth / 2, 0, legWidth, legHeight);
                ctx.strokeRect(-legWidth / 2, 0, legWidth, legHeight);

                ctx.fillStyle = '#95a5a6';
                ctx.beginPath();
                ctx.arc(0, 28, 10, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();

                ctx.fillStyle = '#7f8c8d';
                ctx.fillRect(-footWidth / 2, legHeight - 2, footWidth, footHeight);
                ctx.strokeRect(-footWidth / 2, legHeight - 2, footWidth, footHeight);

                ctx.restore();
            };

            drawLeg(centerX - 122, centerY + 50, leftLegAngle, false);
            drawLeg(centerX + 122, centerY + 50, rightLegAngle, true);

            // === LINE SENSORS (BOTTOM) ===
            // Left sensor
            ctx.fillStyle = '#34495e';
            ctx.beginPath();
            ctx.arc(centerX - 55, centerY + 180, 8, 0, Math.PI * 2);
            ctx.fill();

            // Right sensor
            ctx.beginPath();
            ctx.arc(centerX + 55, centerY + 180, 8, 0, Math.PI * 2);
            ctx.fill();

            // === DISTANCE WAVES (if close) ===
            if (distance < 30) {
                ctx.strokeStyle = '#e74c3c';
                ctx.lineWidth = 2;
                ctx.setLineDash([5, 5]);
                for (let i = 0; i < 3; i++) {
                    ctx.beginPath();
                    ctx.arc(centerX, centerY - 160, 40 + i * 15, -Math.PI / 4, -3 * Math.PI / 4, true);
                    ctx.stroke();
                }
                ctx.setLineDash([]);
            }

            // Animate eyes
            eyeOffset += 0.3 * eyeDirection;
            if (Math.abs(eyeOffset) > 8) {
                eyeDirection *= -1;
            }

            animationId = requestAnimationFrame(drawRobot);
        };

        drawRobot();

        return () => {
            if (animationId) cancelAnimationFrame(animationId);
        };
    }, [distance, ledColor]);// 🔥 REMOVED ledColor dependency - now reads from window.robotLEDState


    const colorPresets = [
        '#FF0000', '#FF8000', '#FFFF00', '#80FF00',
        '#00FF00', '#00FF80', '#00FFFF', '#0080FF',
        '#0000FF', '#7F00FF', '#FF00FF', '#FF007F'
    ];

    return (
        <div style={{ width: '100%', height: '100%', display: 'grid', gridTemplateRows: '2fr 1fr', gap: 0, overflow: 'hidden' }}>
            {/* TOP: BIG BLUE ROBOT CANVAS */}
            <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #5B9BD5 0%, #4A8BC2 100%)', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', padding: '20px' }}>
                <canvas
                    ref={canvasRef}
                    id="otto-robot-canvas"
                    width="800"
                    height="600"
                    style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', boxShadow: '0 15px 50px rgba(0,0,0,0.4)', maxWidth: '100%', maxHeight: '100%' }}
                />

                {/* 🆕 LED RING WIDGET */}
                <LEDRingWidget />
            </div>

            {/* BOTTOM: CONTROLS GRID */}
            <div style={{
                width: '100%',
                height: '100%',
                background: 'white',
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '20px',
                borderTop: '4px solid #4A8BC2'
            }}>x
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '15px',
                    maxWidth: '1400px',
                    margin: '0 auto'
                }}>

                    {/* Temperature */}
                    <div style={{
                        padding: '15px',
                        background: '#F8F9FA',
                        borderRadius: '12px',
                        border: '2px solid #E0E0E0'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '15px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '22px' }}>🔥</span>
                            Temperature: <strong style={{ color: '#FF5722' }}>{temperature}°C</strong>
                        </label>
                        <input
                            type="range"
                            min="-20"
                            max="100"
                            value={temperature}
                            onChange={(e) => setTemperature(Number(e.target.value))}
                            style={{ width: '100%', height: '8px', cursor: 'pointer' }}
                        />
                    </div>

                    {/* Humidity */}
                    <div style={{
                        padding: '15px',
                        background: '#F8F9FA',
                        borderRadius: '12px',
                        border: '2px solid #E0E0E0'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '15px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '22px' }}>💧</span>
                            Humidity: <strong style={{ color: '#2196F3' }}>{humidity}%</strong>
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={humidity}
                            onChange={(e) => setHumidity(Number(e.target.value))}
                            style={{ width: '100%', height: '8px', cursor: 'pointer' }}
                        />
                    </div>

                    {/* Distance */}
                    <div style={{
                        padding: '15px',
                        background: '#F8F9FA',
                        borderRadius: '12px',
                        border: '2px solid #E0E0E0'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '15px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '22px' }}>📏</span>
                            Distance: <strong style={{ color: distance < 30 ? '#e74c3c' : '#4CAF50' }}>{distance}cm</strong>
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="200"
                            value={distance}
                            onChange={(e) => setDistance(Number(e.target.value))}
                            style={{ width: '100%', height: '8px', cursor: 'pointer' }}
                        />
                    </div>

                    {/* Potentiometer */}
                    <div style={{
                        padding: '15px',
                        background: '#F8F9FA',
                        borderRadius: '12px',
                        border: '2px solid #E0E0E0'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '15px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '22px' }}>🔘</span>
                            Potentiometer: <strong style={{ color: '#9C27B0' }}>{potentiometer}</strong>
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="1023"
                            value={potentiometer}
                            onChange={(e) => setPotentiometer(Number(e.target.value))}
                            style={{ width: '100%', height: '8px', cursor: 'pointer' }}
                        />
                    </div>

                    {/* LED Color */}
                    <div style={{
                        padding: '15px',
                        background: '#F8F9FA',
                        borderRadius: '12px',
                        border: '2px solid #E0E0E0'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '15px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '22px' }}>💡</span>
                            LED Color
                        </label>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input
                                type="color"
                                value={ledColor}
                                onChange={(e) => setLedColor(e.target.value)}
                                style={{ width: '50px', height: '35px', cursor: 'pointer', border: 'none' }}
                            />
                            <input
                                type="text"
                                value={ledColor}
                                onChange={(e) => setLedColor(e.target.value)}
                                style={{ flex: 1, padding: '5px', fontSize: '13px', fontFamily: 'monospace' }}
                            />
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px' }}>
                            {colorPresets.map((color, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setLedColor(color)}
                                    style={{
                                        background: color,
                                        height: '25px',
                                        border: ledColor === color ? '3px solid #000' : '1px solid #ccc',
                                        cursor: 'pointer',
                                        borderRadius: '4px'
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Serial Monitor */}
                    <div style={{
                        padding: '15px',
                        background: '#F8F9FA',
                        borderRadius: '12px',
                        border: '2px solid #E0E0E0',
                        gridColumn: 'span 2'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '15px',
                            fontWeight: '600',
                            marginBottom: '8px',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '22px' }}>📺</span>
                            Serial Monitor
                        </label>
                        <div style={{
                            background: '#1E1E1E',
                            color: '#00FF00',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            padding: '10px',
                            borderRadius: '6px',
                            height: '120px',
                            overflowY: 'auto',
                            marginBottom: '8px'
                        }}>
                            {serialOutput.length === 0 ? (
                                <div style={{ color: '#666' }}>Waiting for output...</div>
                            ) : (
                                serialOutput.map((line, idx) => <div key={idx}>{line}</div>)
                            )}
                        </div>
                        <button
                            onClick={() => setSerialOutput([])}
                            style={{
                                padding: '8px 16px',
                                background: '#FF5252',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Clear
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default RobotControl;
